import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  RadialBarChart,
  RadialBar,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import {
  ArrowLeft,
  Crown,
  TrendingUp,
  Users,
  Star,
  UserCheck,
  BookOpen,
  Calendar,
  Lock,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const COLORS = ["#13A699", "#FFD708", "#3B82F6", "#EC4899", "#F59E0B", "#DC2626"];
const RATING_COLORS = { punctuality: "#13A699", fluency: "#FFD708", content: "#3B82F6", availability: "#EC4899" };

function formatINR(n) {
  const v = Number(n || 0);
  return "₹" + v.toLocaleString("en-IN");
}

function Kpi({ icon: Icon, label, value, sub, testid, accent = "#13A699" }) {
  return (
    <div
      className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm"
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <div className="p-2 rounded-xl" style={{ background: accent + "20", color: accent }}>
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: accent }}>{label}</span>
      </div>
      <div className="mt-3 text-2xl font-display font-bold" style={{ color: "#13A699" }}>{value}</div>
      {sub && <div className="text-xs mt-1 opacity-70" style={{ color: "#13A699" }}>{sub}</div>}
    </div>
  );
}

function LockedGate() {
  return (
    <div className="min-h-screen py-16 px-4" style={{ background: "#FFF7ED" }}>
      <div className="max-w-2xl mx-auto bg-white rounded-3xl p-10 text-center border-2 border-[#FFD708]" data-testid="analytics-locked">
        <Crown className="h-12 w-12 mx-auto mb-4" style={{ color: "#FFD708" }} fill="#FFD708" />
        <h1 className="font-display text-3xl font-bold uppercase mb-2" style={{ color: "#13A699" }}>
          Analytics is a Pro perk
        </h1>
        <p className="text-sm mb-6" style={{ color: "#13A699" }}>
          Upgrade to Mentor Professional (₹999/mo) to unlock this dashboard, plus higher search ranking, verified badge, attendance tracker and priority support.
        </p>
        <Link
          to="/teacher/dashboard"
          className="btn-yellow text-sm px-5 py-2.5 inline-flex items-center gap-2"
          data-testid="analytics-upgrade-link"
        >
          <TrendingUp className="h-4 w-4" /> Upgrade my plan
        </Link>
      </div>
    </div>
  );
}

export default function MentorAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [aRes, attRes] = await Promise.all([
          api.get("/mentor/analytics"),
          api.get("/mentor/attendance"),
        ]);
        setData(aRes.data);
        setAttendance(attRes.data);
      } catch (err) {
        if (err?.response?.status === 402) {
          setLocked(true);
        } else {
          toast.error(formatApiError(err));
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const ratingBars = useMemo(() => {
    if (!data?.rating_breakdown) return [];
    const rb = data.rating_breakdown;
    return [
      { attr: "Punctuality", value: Number(rb.punctuality?.avg || 0), fill: RATING_COLORS.punctuality, count: rb.punctuality?.count || 0 },
      { attr: "Fluency", value: Number(rb.fluency?.avg || 0), fill: RATING_COLORS.fluency, count: rb.fluency?.count || 0 },
      { attr: "Content", value: Number(rb.content?.avg || 0), fill: RATING_COLORS.content, count: rb.content?.count || 0 },
      { attr: "Availability", value: Number(rb.availability?.avg || 0), fill: RATING_COLORS.availability, count: rb.availability?.count || 0 },
    ];
  }, [data]);

  const attendancePie = useMemo(() => {
    if (!data?.kpis) return [];
    const att = data.kpis.attendance_attended || 0;
    const missed = Math.max(0, (data.kpis.attendance_scheduled || 0) - att);
    return [
      { name: "Attended", value: att },
      { name: "Missed", value: missed },
    ];
  }, [data]);

  const downloadCsv = () => {
    if (!attendance?.rows) return;
    const header = ["Date", "Time", "Parent", "Status", "Teacher Present", "Parent Present", "On Time"];
    const rows = attendance.rows.map((r) => [
      r.date,
      `${r.start_time}-${r.end_time}`,
      r.parent_name,
      r.status,
      r.teacher_present ? "Yes" : "No",
      r.parent_present ? "Yes" : "No",
      r.on_time ? "Yes" : "No",
    ]);
    const csv = [header, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (locked) return <LockedGate />;

  if (loading || !data) {
    return (
      <div className="min-h-screen py-12 px-4" style={{ background: "#FFF7ED" }}>
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-64 bg-black/5 rounded" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[0, 1, 2, 3].map((i) => <div key={i} className="h-28 bg-white rounded-2xl border border-black/5" />)}
            </div>
            <div className="h-72 bg-white rounded-2xl border border-black/5" />
          </div>
        </div>
      </div>
    );
  }

  const k = data.kpis || {};
  return (
    <div className="min-h-screen py-10 px-4" style={{ background: "#FFF7ED" }} data-testid="mentor-analytics-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <Link to="/teacher/dashboard" className="inline-flex items-center gap-1 text-xs font-bold uppercase" style={{ color: "#13A699" }}>
              <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
            </Link>
            <h1 className="font-display text-4xl sm:text-5xl font-bold uppercase mt-2 flex items-center gap-3" style={{ color: "#13A699" }}>
              Analytics
              <Crown className="h-8 w-8" style={{ color: "#FFD708" }} fill="#FFD708" />
            </h1>
            <p className="text-sm mt-1 opacity-70" style={{ color: "#13A699" }}>
              Live performance for <b>{user?.name || "Mentor"}</b> · updated {new Date(data.generated_at).toLocaleString("en-IN")}
            </p>
          </div>
          <button
            onClick={downloadCsv}
            disabled={!attendance?.rows?.length}
            className="text-sm px-4 py-2 rounded-full border border-[#13A699] text-[#13A699] inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="analytics-download-attendance"
          >
            <Download className="h-4 w-4" /> Attendance CSV
          </button>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi
            icon={TrendingUp}
            label="Total Earnings"
            value={formatINR(k.total_earnings_inr)}
            sub={`${formatINR(k.earnings_this_month_inr)} this month`}
            testid="kpi-earnings"
          />
          <Kpi
            icon={BookOpen}
            label="Total Bookings"
            value={k.total_bookings}
            sub={`${k.completed_bookings} completed`}
            testid="kpi-bookings"
            accent="#3B82F6"
          />
          <Kpi
            icon={Star}
            label="Avg Rating"
            value={Number(k.avg_rating || 0).toFixed(2)}
            sub={`from ${k.rating_count} rating${k.rating_count === 1 ? "" : "s"}`}
            testid="kpi-rating"
            accent="#F59E0B"
          />
          <Kpi
            icon={UserCheck}
            label="Attendance"
            value={`${k.attendance_rate_pct}%`}
            sub={`${k.attendance_attended} / ${k.attendance_scheduled} sessions`}
            testid="kpi-attendance"
            accent="#EC4899"
          />
        </div>

        {/* Earnings trend */}
        <div className="mt-8 bg-white rounded-3xl p-6 border border-black/5 shadow-sm" data-testid="chart-earnings">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4" style={{ color: "#13A699" }} />
            <h3 className="font-display font-bold uppercase" style={{ color: "#13A699" }}>Earnings — Last 6 Months</h3>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={data.monthly_earnings}>
                <defs>
                  <linearGradient id="grad-earn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#13A699" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#13A699" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
                <XAxis dataKey="month" stroke="#13A699" fontSize={11} />
                <YAxis stroke="#13A699" fontSize={11} tickFormatter={(v) => `₹${v.toLocaleString("en-IN")}`} />
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 12, borderColor: "#13A699" }} />
                <Area type="monotone" dataKey="earnings_inr" stroke="#13A699" strokeWidth={2} fill="url(#grad-earn)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bookings + Rating trend row */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl p-6 border border-black/5 shadow-sm" data-testid="chart-bookings">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-4 w-4" style={{ color: "#13A699" }} />
              <h3 className="font-display font-bold uppercase" style={{ color: "#13A699" }}>Bookings — Last 6 Months</h3>
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={data.monthly_bookings}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
                  <XAxis dataKey="month" stroke="#13A699" fontSize={11} />
                  <YAxis stroke="#13A699" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, borderColor: "#13A699" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="completed" stackId="a" fill="#13A699" name="Completed" />
                  <Bar dataKey="confirmed" stackId="a" fill="#FFD708" name="Confirmed" />
                  <Bar dataKey="cancelled" stackId="a" fill="#DC2626" name="Cancelled" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 border border-black/5 shadow-sm" data-testid="chart-rating-trend">
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-4 w-4" style={{ color: "#F59E0B" }} />
              <h3 className="font-display font-bold uppercase" style={{ color: "#13A699" }}>Rating Trend</h3>
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={data.monthly_rating}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
                  <XAxis dataKey="month" stroke="#13A699" fontSize={11} />
                  <YAxis stroke="#13A699" fontSize={11} domain={[0, 5]} />
                  <Tooltip contentStyle={{ borderRadius: 12, borderColor: "#13A699" }} />
                  <Line type="monotone" dataKey="avg" stroke="#F59E0B" strokeWidth={3} dot={{ r: 5 }} name="Avg Rating" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Rating breakdown + attendance donut */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl p-6 border border-black/5 shadow-sm" data-testid="chart-rating-breakdown">
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-4 w-4" style={{ color: "#F59E0B" }} />
              <h3 className="font-display font-bold uppercase" style={{ color: "#13A699" }}>Rating Breakdown</h3>
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={ratingBars} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
                  <XAxis type="number" domain={[0, 5]} stroke="#13A699" fontSize={11} />
                  <YAxis type="category" dataKey="attr" stroke="#13A699" fontSize={11} width={90} />
                  <Tooltip contentStyle={{ borderRadius: 12, borderColor: "#13A699" }} />
                  <Bar dataKey="value" name="Avg" radius={[0, 8, 8, 0]}>
                    {ratingBars.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 border border-black/5 shadow-sm" data-testid="chart-attendance">
            <div className="flex items-center gap-2 mb-4">
              <UserCheck className="h-4 w-4" style={{ color: "#EC4899" }} />
              <h3 className="font-display font-bold uppercase" style={{ color: "#13A699" }}>Attendance</h3>
            </div>
            {(k.attendance_scheduled || 0) === 0 ? (
              <div className="text-sm py-16 text-center opacity-60" style={{ color: "#13A699" }}>
                No completed sessions yet — punch in from your dashboard to start tracking.
              </div>
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={attendancePie} innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="value" nameKey="name">
                      <Cell fill="#13A699" />
                      <Cell fill="#DC2626" />
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, borderColor: "#13A699" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Top courses */}
        <div className="mt-6 bg-white rounded-3xl p-6 border border-black/5 shadow-sm" data-testid="top-courses">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="h-4 w-4" style={{ color: "#13A699" }} />
            <h3 className="font-display font-bold uppercase" style={{ color: "#13A699" }}>Top Courses</h3>
          </div>
          {(!data.top_courses || data.top_courses.length === 0) ? (
            <div className="text-sm py-6 text-center opacity-60" style={{ color: "#13A699" }}>No course enrollments yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left" style={{ color: "#13A699" }}>
                    <th className="py-2 pr-4 font-bold">Course</th>
                    <th className="py-2 pr-4 font-bold">Enrollments</th>
                    <th className="py-2 pr-4 font-bold">Gross</th>
                    <th className="py-2 pr-4 font-bold">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_courses.map((c) => (
                    <tr key={c.course_id} className="border-t border-black/5">
                      <td className="py-2 pr-4">{c.course_title}</td>
                      <td className="py-2 pr-4">{c.enrollments}</td>
                      <td className="py-2 pr-4">{formatINR(c.gross_inr)}</td>
                      <td className="py-2 pr-4">{c.rating_avg ? c.rating_avg.toFixed(2) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Attendance tracker table */}
        <div className="mt-6 bg-white rounded-3xl p-6 border border-black/5 shadow-sm" data-testid="attendance-tracker">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" style={{ color: "#EC4899" }} />
              <h3 className="font-display font-bold uppercase" style={{ color: "#13A699" }}>Attendance Tracker</h3>
            </div>
            <span className="text-xs opacity-60" style={{ color: "#13A699" }}>{attendance?.total || 0} sessions</span>
          </div>
          {(!attendance || attendance.rows.length === 0) ? (
            <div className="text-sm py-6 text-center opacity-60" style={{ color: "#13A699" }}>No sessions yet.</div>
          ) : (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="sticky top-0" style={{ background: "#FFF7ED" }}>
                  <tr className="text-left" style={{ color: "#13A699" }}>
                    <th className="py-2 pr-4 font-bold">Date</th>
                    <th className="py-2 pr-4 font-bold">Time</th>
                    <th className="py-2 pr-4 font-bold">Parent</th>
                    <th className="py-2 pr-4 font-bold">Status</th>
                    <th className="py-2 pr-4 font-bold">Mentor</th>
                    <th className="py-2 pr-4 font-bold">Explorer</th>
                    <th className="py-2 pr-4 font-bold">On time</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.rows.slice(0, 100).map((r) => (
                    <tr key={r.booking_id} className="border-t border-black/5">
                      <td className="py-2 pr-4">{r.date}</td>
                      <td className="py-2 pr-4">{r.start_time}–{r.end_time}</td>
                      <td className="py-2 pr-4">{r.parent_name}</td>
                      <td className="py-2 pr-4">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ background: r.status === "completed" ? "#D1FAE5" : "#FEF3C7", color: r.status === "completed" ? "#065F46" : "#92400E" }}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-2 pr-4">{r.teacher_present ? "✓" : "—"}</td>
                      <td className="py-2 pr-4">{r.parent_present ? "✓" : "—"}</td>
                      <td className="py-2 pr-4">{r.on_time ? <span className="text-green-700 font-bold">✓</span> : <span className="text-red-600">✗</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
